import React, { memo, useContext } from 'react'
import { ControlledSwitch, ControlledTextArea } from 'transfix-form/components'
import ControlledCurrencyInput from 'transfix-form/components/ControlledCurrencyInput'
import ControlledRadioGroupInput, {
  IRadioOption,
} from 'transfix-form/components/ControlledRadioGroupInput'
import ControlledTextInput from 'transfix-form/components/ControlledTextInput'
import { FormHelperText } from 'transfix-ui/components/FormHelperText'
import { Box } from 'transfix-ui/core'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import ControlledShipmentAutosuggest from 'apps/marketplace/pages/shipment-details/components/ShipmentProgress/components/StopBasicInfo/AddTrailer/ControlledShipmentAutosuggest'
import { InfoIcon } from 'transfix-icons'
import { brandColors, Tooltip } from 'transfix-ui'
import { useCreateShipmentFormContext } from '../../../context'
import { OriginalShipmentContext } from '../../../context/OriginalShipmentProvider'
import { getShipmentErrorMessage } from '../../../utils'
import CalculatedMilesWarning from './CalculatedMilesWarning'
import { SHIPPER_RATE_TYPE_OPTIONS } from './constants'

export interface IShipmentTermsFormSectionProps {
  shipperApplicationId: string
}

const useShipmentTermsFormSectionStyles = makeStyles(theme => ({
  switchWrapper: {
    paddingLeft: theme.spacing(1.5),
  },
  isRelatedContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  relatedShipmentNote: {
    color: brandColors.coolGray5,
  },
  relatedShipmentLabel: {
    display: 'flex',
    flexDirection: 'row',
  },
}))

const getNumberValue = (val: string) => Number(val) || undefined

export const isRelatedOptions: IRadioOption[] = [
  { id: false, label: 'No' },
  { id: true, label: 'Yes' },
]

const ShipmentTermsFormSection = memo<IShipmentTermsFormSectionProps>(
  ({ shipperApplicationId }) => {
    const classes = useShipmentTermsFormSectionStyles()
    /**
     * Form Context
     */
    const { control, errors, watch } = useCreateShipmentFormContext()
    const { shipment: originalShipment } = useContext(OriginalShipmentContext)
    const isRelatedShipmentsEnabled = useFeatureFlag('mpRelatedShipments')
    const { shipment } = watch(['shipment'])
    const { isRelatedShipment } = shipment || {}

    /**
     * Errors
     */
    const { shipment: shipmentErrors } = errors || {}

    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography variant='h3'>Shipment Terms</Typography>
        </Grid>
        <Grid item xs={12} xl={6}>
          <ControlledTextInput
            control={control}
            name='shipment.totalWeight'
            TextInputProps={{
              type: 'number',
              label: 'Total Weight',
              placeholder: 'lbs',
              dataTest: 'total-weight-field',
              error: !!getShipmentErrorMessage(shipmentErrors, 'totalWeight'),
              validationMessage: getShipmentErrorMessage(shipmentErrors, 'totalWeight'),
              required: true,
            }}
          />
        </Grid>
        <Grid item xs={12} xl={6}>
          <ControlledTextInput
            control={control}
            name='shipment.totalPallets'
            TextInputProps={{
              type: 'number',
              label: 'Total Pallets',
              placeholder: '#',
              dataTest: 'total-pallets-field',
              error: !!getShipmentErrorMessage(shipmentErrors, 'totalPallets'),
              validationMessage: getShipmentErrorMessage(shipmentErrors, 'totalPallets'),
              required: true,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledCurrencyInput
            control={control}
            name='shipment.shipperPerMileFuelRate'
            CurrencyInputProps={{
              label: 'Fuel/Mile Rate',
              dataTest: 'shipper-per-mile-fuel-rate-field',
              invalid: !!getShipmentErrorMessage(shipmentErrors, 'shipperPerMileFuelRate'),
              validationMessage: getShipmentErrorMessage(shipmentErrors, 'shipperPerMileFuelRate'),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledTextInput
            control={control}
            name='shipment.shipperMiles'
            mutateValue={getNumberValue}
            TextInputProps={{
              type: 'number',
              label: 'Miles',
              dataTest: 'shipper-miles-field',
              error: !!getShipmentErrorMessage(shipmentErrors, 'shipperMiles'),
              validationMessage: getShipmentErrorMessage(shipmentErrors, 'shipperMiles'),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <CalculatedMilesWarning />
        </Grid>
        <Grid item xs={12}>
          <ControlledCurrencyInput
            control={control}
            name='shipment.shipperRate'
            CurrencyInputProps={{
              label: 'Customer Rate',
              dataTest: 'shipper-rate-field',
              invalid: !!getShipmentErrorMessage(shipmentErrors, 'shipperRate'),
              validationMessage: getShipmentErrorMessage(shipmentErrors, 'shipperRate'),
              hideRequiredLabel: true,
              required: true,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <label className='label required' htmlFor='shipperRateType'>
            Rate Type
          </label>
          <ControlledRadioGroupInput
            control={control}
            name='shipment.shipperRateType'
            options={SHIPPER_RATE_TYPE_OPTIONS}
            RadioProps={{
              dataTest: 'shipper-rate-type-field',
            }}
          />
          {!!getShipmentErrorMessage(shipmentErrors, 'shipperRateType') && (
            <FormHelperText className='Mui-error'>
              {getShipmentErrorMessage(shipmentErrors, 'shipperRateType')}
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12}>
          <ControlledTextArea
            control={control}
            name='shipment.carrierSpecialInstructions'
            TextAreaProps={{
              label: 'Internal Notes',
              dataTest: 'carrier-special-instructions-field',
              size: 'small',
              invalid: !!getShipmentErrorMessage(shipmentErrors, 'carrierSpecialInstructions'),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <div className={classes.switchWrapper}>
            <ControlledSwitch
              control={control}
              name='shipment.team'
              SwitchProps={{
                label: 'Team Shipment',
                dataTest: 'team-field',
              }}
            />
            <ControlledSwitch
              control={control}
              name='shipment.floorLoaded'
              SwitchProps={{
                label: 'Floor Loaded',
                dataTest: 'floor-loaded-field',
              }}
            />
            <ControlledSwitch
              control={control}
              name='shipment.hot'
              SwitchProps={{
                label: 'Hot Shipment (Upgrade to Level 1 Tracking)',
                dataTest: 'team-field',
              }}
            />
          </div>
        </Grid>
        {isRelatedShipmentsEnabled && (
          <Grid item xs={12}>
            <div className={classes.isRelatedContainer}>
              <div className={classes.relatedShipmentLabel}>
                <Box width='50%' display='flex' alignItems='center'>
                  <label className='label' htmlFor='isRelatedShipment'>
                    Is this a related shipment?
                  </label>
                  <Tooltip
                    position='top'
                    id='relatedShipmentsTooltip'
                    trigger={
                      <Box display='flex' alignItems='center' marginLeft={0.5}>
                        <InfoIcon color='coolGray5' size='large' />
                      </Box>
                    }>
                    Crossdocks; return loads and other types of OOP loads
                  </Tooltip>
                </Box>
                <Box width='50%'>
                  <label
                    className={`label ${classes.relatedShipmentNote}`}
                    htmlFor='isRelatedShipment'>
                    Use this when shipment is not the primary shipment for the shipper
                  </label>
                </Box>
              </div>
              <ControlledRadioGroupInput
                control={control}
                name='shipment.isRelatedShipment'
                options={isRelatedOptions}
                defaultValue={Boolean(originalShipment)}
                RadioProps={{
                  dataTest: 'is-related-shipment',
                }}
              />
            </div>
            {(isRelatedShipment ?? originalShipment) && (
              <ControlledShipmentAutosuggest
                name='shipment.originalShipmentId'
                control={control}
                shipment={originalShipment}
                filters={{
                  shipperApplicationIds: [shipperApplicationId],
                }}
                minimumSearchLength={3}
                AutocompleteProps={{
                  label: 'Original Shipment',
                  inputValidationMessage: getShipmentErrorMessage(
                    shipmentErrors,
                    'originalShipmentId'
                  ),
                  inputError: !!getShipmentErrorMessage(shipmentErrors, 'originalShipmentId'),
                }}
              />
            )}
          </Grid>
        )}
      </Grid>
    )
  }
)

export default ShipmentTermsFormSection
